import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { FpDownloadButtons } from "@/pages/panchatanthra/fp/FpDownloadButtons";
import { EmptyState } from "@/components/shared";
import { LEVEL_TONE, fpBase, inr, monthLabel, useFpContext } from "@/lib/fp";
import { toast } from "sonner";

const AVAIL_TONE = {
  Available: "bg-success/18 text-success",
  "Partially available": "bg-warning/25 text-[hsl(30_88%_30%)]",
  "Not available": "bg-destructive/15 text-destructive",
};

/** The design's Sources sheet: what breakup the client can actually supply, per axis. */
const SourceAvailability = ({ tenantId }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});

  const key = (r) => `${r.particular}|${r.breakup_level}`;

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const { data: out } = await api.get(`${fpBase(tenantId)}/source-availability`);
      setData(out);
      setDraft(Object.fromEntries(out.rows.map((r) =>
        [key(r), { availability: r.availability || "", source: r.source || "",
                   notes: r.notes || "" }])));
    } catch (e) { setData({ error: errMsg(e) }); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.put(`${fpBase(tenantId)}/source-availability`, {
        items: data.rows.map((r) => ({ particular: r.particular,
                                       breakup_level: r.breakup_level, ...draft[key(r)] })),
      });
      toast.success("Source availability saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data || data.error) return null;
  const set = (r, patch) => setDraft({ ...draft, [key(r)]: { ...draft[key(r)], ...patch } });

  return (
    <div className="panel overflow-hidden" data-testid="fp-source-availability">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
        <div>
          <p className="label-caps">Source availability</p>
          <h3 className="font-display text-lg text-navy">
            Which breakup this client can actually give us
          </h3>
        </div>
        {data.can_configure && (
          <button onClick={save} data-testid="fp-availability-save"
                  className="rounded-full bg-terracotta px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-terracotta/90">
            Save
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
            <tr>
              <th className="px-5 py-2.5 text-left">Particulars</th>
              <th className="px-5 py-2.5 text-left">Breakup level</th>
              <th className="px-5 py-2.5 text-left">Availability</th>
              <th className="px-5 py-2.5 text-left">Where it comes from</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.rows.map((r) => {
              const d = draft[key(r)] || {};
              const testid = `${r.particular}-${r.breakup_level}`.replace(/\W+/g, "-").toLowerCase();
              return (
                <tr key={key(r)} data-testid={`fp-availability-row-${testid}`}>
                  <td className="px-5 py-2.5 font-medium text-navy">{r.particular}</td>
                  <td className="px-5 py-2.5 text-foreground/80">{r.breakup_level}</td>
                  <td className="px-5 py-2.5">
                    {data.can_configure ? (
                      <select value={d.availability || ""} data-testid={`fp-availability-${testid}`}
                              onChange={(e) => set(r, { availability: e.target.value })}
                              className="rounded-full border border-border bg-card px-3 py-1.5 text-xs text-navy outline-none focus:border-terracotta">
                        <option value="">Not stated</option>
                        {data.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <span className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.1em] ${
                        AVAIL_TONE[d.availability] || "bg-muted text-muted-foreground"}`}>
                        {d.availability || "Not stated"}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-2.5">
                    <input value={d.source || ""} placeholder="e.g. Tally P&L tags, sales register"
                           disabled={!data.can_configure}
                           data-testid={`fp-availability-source-${testid}`}
                           onChange={(e) => set(r, { source: e.target.value })}
                           className="w-full rounded-lg border border-border bg-card px-3 py-1.5 text-xs text-navy outline-none focus:border-terracotta disabled:opacity-60" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function FpReadiness() {
  const { tenantId, period, basis } = useFpContext();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId || !period) return;
    try {
      const { data: out } = await api.get(`${fpBase(tenantId)}/readiness`,
                                          { params: { period, basis } });
      setData(out);
    } catch (e) { setData({ error: errMsg(e) }); }
  }, [tenantId, period, basis]);

  useEffect(() => { load(); }, [load]);

  return (
    <FpShell
      title="Financial data readiness"
      description="The engine generates the highest level the data supports and states exactly what is missing for Level 3 — with a short-term Aikyam interface and a long-term system change for each gap."
      actions={<FpDownloadButtons />}
    >
      {!period ? (
        <EmptyState testid="fp-readiness-empty" title="No statements yet"
                    message="Upload a month of financial statements to assess readiness." />
      ) : data?.error ? (
        <EmptyState testid="fp-readiness-error" title="Unavailable" message={data.error} />
      ) : !data ? (
        <p className="label-caps animate-pulse">Assessing readiness</p>
      ) : (
        <div className="space-y-6" data-testid="fp-readiness">
          <div className="panel p-6">
            <div className="flex flex-wrap items-end justify-between gap-5">
              <div>
                <p className="label-caps">Current capability · {data.fiscal_year} ·
                  {" "}{basis === "ytd" ? "cumulative to" : "month of"} {monthLabel(period)}</p>
                <p className="mt-2 font-display text-3xl text-navy" data-testid="fp-readiness-overall">
                  {data.readiness.overall_level}
                </p>
              </div>
              <div className="text-right">
                <p className="label-caps">Level-3 coverage</p>
                <p className="font-mono text-4xl tabular-nums text-navy" data-testid="fp-readiness-coverage">
                  {data.readiness.coverage_pct}%
                </p>
              </div>
            </div>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-terracotta"
                   style={{ width: `${Math.min(data.readiness.coverage_pct, 100)}%` }} />
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Coverage weighting is Aikyam configuration, not hard-coded — edit it under
              Administration → Financial engine configuration → readiness rules.
            </p>
          </div>

          <SourceAvailability tenantId={tenantId} />

          <div className="space-y-4">
            {data.readiness.areas.map((a) => (
              <div key={a.area} className="panel overflow-hidden"
                   data-testid={`fp-readiness-area-${a.area.toLowerCase().replace(/\W+/g, "-")}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
                  <div>
                    <h3 className="font-display text-lg text-navy">{a.area}</h3>
                    <p className="text-[11px] text-muted-foreground">{a.level3_requirement}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="label-caps">weight {a.coverage_weight}</span>
                    <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.1em] ${LEVEL_TONE[a.level]}`}>
                      {a.level}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 px-6 py-4 md:grid-cols-3">
                  <div>
                    <p className="label-caps">Assessment</p>
                    <p className="mt-1 text-sm text-navy">{a.reason}</p>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Reconciled: {a.reconciled ? "yes" : "no"}
                    </p>
                    {a.control_detail?.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {a.control_detail.map((p) => (
                          <li key={p.control_item} className="text-[11px]"
                              data-testid={`fp-readiness-pair-${p.control_item.toLowerCase().replace(/\W+/g, "-")}`}>
                            <span className="text-navy">{p.control_item}</span>{" — "}
                            <span className="text-muted-foreground">
                              Tally control{" "}
                              <span className="font-mono tabular-nums">
                                {p.control_total === null ? "none" : inr(p.control_total)}
                              </span>
                              {" · interface detail "}
                              <span className="font-mono tabular-nums">
                                {p.detail_total === null ? "none" : inr(p.detail_total)}
                              </span>
                            </span>
                            {(p.control_documents?.length > 0 || p.detail_documents?.length > 0) && (
                              <span className="block text-[9px] text-muted-foreground/70">
                                {[...new Set([...(p.control_documents || []),
                                              ...(p.detail_documents || [])])].join(", ")}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <p className="label-caps">Short-term remedy</p>
                    <p className="mt-1 text-sm text-navy">{a.interface_remedy}</p>
                  </div>
                  <div>
                    <p className="label-caps">Long-term system remedy</p>
                    <p className="mt-1 text-sm text-navy">{a.system_remedy}</p>
                  </div>
                </div>
                {a.level !== "Level 3" && (
                  <p className="border-t border-border bg-warning/10 px-6 py-3 text-[11px] text-[hsl(30_88%_28%)]">
                    {a.recommendation}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </FpShell>
  );
}
